import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useNavigate, useSearchParams } from "react-router-dom";
import AuthLayout from "./AuthLayout";
import { AuthField, AuthSubmit, authLinkClass } from "./AuthField";
import { useScriptMode } from "../../contexts/ScriptModeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useVerifyEmailCodeMutation, useSendVerificationCodeMutation } from "../../api/authApi";
import { useToast } from "../../components/ui/Toast";

export default function EmailVerifyPage() {
  const { t } = useScriptMode();
  const { setSession } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [params] = useSearchParams();
  const email = params.get("email") || "";

  const [resendCountdown, setResendCountdown] = useState(0);

  useEffect(() => {
    if (!email) navigate("/giris", { replace: true });
  }, [email, navigate]);

  const schema = yup.object({
    code: yup.string().length(6, t("validation_code_length")).required(t("validation_required")),
  });
  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { code: "" },
  });

  const [verifyEmailCode, { isLoading }] = useVerifyEmailCodeMutation();
  const [sendVerificationCode] = useSendVerificationCodeMutation();

  const onSubmit = async (values) => {
    const result = await verifyEmailCode({ email, verificationCode: values.code });
    if (result.error) {
      toast.error(result.error.message || t("verify_error"));
      return;
    }
    const data = result.data?.data;
    if (data?.token) {
      setSession({ token: data.token, user: data.user });
      navigate("/uygulama/profil", { replace: true });
    } else {
      toast.success(t("verify_success"));
      navigate("/giris", { replace: true });
    }
  };

  const startResendCountdown = () => {
    setResendCountdown(60);
    const interval = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) { clearInterval(interval); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResend = async () => {
    if (resendCountdown > 0) return;
    const result = await sendVerificationCode({ email });
    if (result.error) { toast.error(result.error.message || t("verify_code_send_error")); return; }
    toast.success(t("verify_code_sent"));
    startResendCountdown();
  };

  return (
    <AuthLayout title={t("verify_title")} subtitle={t("verify_subtitle", { email })}>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
        <AuthField
          id="verify-code"
          label={t("verify_code")}
          maxLength={6}
          inputMode="numeric"
          pattern="[0-9]*"
          placeholder="000000"
          className="text-center text-lg tracking-[0.6em]"
          disabled={isLoading}
          error={errors.code?.message}
          {...register("code")}
        />
        <AuthSubmit loading={isLoading}>{t("verify_submit")}</AuthSubmit>
        <button type="button" onClick={handleResend} disabled={resendCountdown > 0} className={`text-center text-base ${authLinkClass}`}>
          {resendCountdown > 0 ? t("verify_resend_in", { seconds: resendCountdown }) : t("verify_resend")}
        </button>
      </form>
    </AuthLayout>
  );
}
